import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { getMargin, setMargin, type MarginConfig } from '@/api/pricing'
import { ApiError } from '@/api/client'
import { EditableField } from '@/components/EditableField'
import { Panel } from '@/components/Panel'
import { fmtPct } from '@/lib/pricing-format'

interface Props {
  sku: string
}

/** Editierbare Gewinn-Margen eines Grundartikels. UI in %, Speicherung als Bruch. */
export function MarginSection({ sku }: Props) {
  const queryClient = useQueryClient()
  const { data: margin, isLoading } = useQuery({
    queryKey: ['pricing-margin', sku],
    queryFn: () => getMargin(sku),
    staleTime: 30_000,
  })

  const mutation = useMutation({
    mutationFn: (next: MarginConfig) => setMargin(sku, next),
    onSuccess: (fresh: MarginConfig) => {
      queryClient.setQueryData(['pricing-margin', sku], fresh)
      toast.success('Marge gespeichert')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen'),
  })

  // UI zeigt Prozent (20), speichert Bruch (0.2). Eingabe-Prozent / 100 -> Bruch.
  function pctToFraction(v: string | number | boolean | null | undefined): number {
    const num = typeof v === 'number' ? v : Number(v)
    if (Number.isNaN(num)) throw new Error('Keine gültige Zahl')
    return Math.round((num / 100) * 10000) / 10000
  }

  return (
    <Panel title="Gewinn-Margen" subtitle="Gilt für alle Varianten dieses Grundartikels. Eingabe in Prozent.">
      {isLoading || !margin ? (
        <div className="py-6 text-center text-ink-2">Lade…</div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <EditableField
              type="number"
              step="0.5"
              label="Marge mit Versand"
              hint={`Aktuell: ${fmtPct(margin.with_shipping)} · treibt den Verkaufspreis inkl. Versand`}
              mono
              value={Math.round(margin.with_shipping * 10000) / 100}
              onSave={async (v) => {
                await mutation.mutateAsync({
                  with_shipping: pctToFraction(v),
                  without_shipping: margin.without_shipping,
                })
              }}
            />
            <EditableField
              type="number"
              step="0.5"
              label="Marge ohne Versand"
              hint={`Aktuell: ${fmtPct(margin.without_shipping)}`}
              mono
              value={Math.round(margin.without_shipping * 10000) / 100}
              onSave={async (v) => {
                await mutation.mutateAsync({
                  with_shipping: margin.with_shipping,
                  without_shipping: pctToFraction(v),
                })
              }}
            />
          </div>
          {/* Vorab-Datenpflege (Spec §10/3): wirkt heute nicht auf Preise — reiner Hinweis, kein Verbot. */}
          {margin.without_shipping < margin.with_shipping && (
            <p role="note" className="rounded-md bg-warn-soft px-3 py-2 text-xs text-warn">
              Marge ohne Versand liegt unter Marge mit Versand — ab der Plus-Versand-Formel (Schnitt A) unterschreiten diese Listings den Amazon-Leitpreis (Parität). Heute ohne Preiswirkung.
            </p>
          )}
        </div>
      )}
    </Panel>
  )
}
